import { useState, useEffect, useRef, useCallback } from 'react';
import GoBoard from './components/GoBoard/GoBoard';
import Review from './components/Review/Review';
import Auth from './components/Auth/Auth';
import { io, Socket } from 'socket.io-client';
import { StoneColor, Coordinate, SoundManager } from '@go-platform/go-engine';
import { SERVER_URL, UserInfo, TOKEN_KEY, rankLabel } from './api';
import './App.css';

/** 服务端 roomState 结构 */
interface ClockSide { main: number; byoyomi: number; periods: number; }
interface ClockState {
  current: 'black' | 'white';
  mainTime: number;
  byoyomiTime: number;
  black: ClockSide;
  white: ClockSide;
}
interface RoomState {
  code: string;
  status: 'waiting' | 'playing' | 'finished';
  boardSize: number;
  black: string | null;
  white: string | null;
  spectators: string[];
  currentPlayer: 'black' | 'white' | null;
  board: number[][] | null;
  captureRule: number;
  captures: { black: number; white: number } | null;
  result: { winner: string; reason: string; blackScore?: number; whiteScore?: number; margin?: number } | null;
  moveCount: number;
  moveLog: { x: number | null; y: number | null; color: string }[];
  clock: ClockState | null;
  pendingUndo: boolean;
  pendingDraw: boolean;
  pendingCount: { result: { black: number; white: number; dead: Coordinate[]; territory?: { black: Coordinate[]; white: Coordinate[] } }; from: string } | null;
  rankChanges: { side: 'black' | 'white'; before: number; after: number; beforeLabel: string; afterLabel: string; delta: number; reason: string }[] | null;
  problem: { title: string; goal: string } | null;
}

type TimeControl = 'none' | 'blitz' | 'rapid' | 'slow';

const TIME_CONTROL_LABEL: Record<TimeControl, string> = {
  none: '不限时',
  blitz: '快棋 1分+30秒×3',
  rapid: '10分+30秒×3',
  slow: '30分+30秒×3',
};

type View = 'lobby' | 'game' | 'review' | 'problems';
type Mode = 'create' | 'join' | 'spectate' | 'ai';
type Size = 13 | 19;
type AuthState = 'loading' | 'unauthenticated' | 'authenticated';

const REASON_TEXT: Record<string, string> = {
  counting: '数子',
  resign: '认输',
  draw: '和棋',
  timeout: '超时',
};

/** 对比前后棋盘，返回发生变化的坐标（作为最后一步高亮） */
function diffBoard(prev: number[][] | null, next: number[][] | null): Coordinate | null {
  if (!prev || !next) return null;
  const size = next.length;
  for (let y = 0; y < size; y++) {
    const row = next[y];
    const prevRow = prev[y];
    for (let x = 0; x < row.length; x++) {
      if (!prevRow || prevRow[x] !== row[x]) {
        return { x: x + 1, y: y + 1 };
      }
    }
  }
  return null;
}

/** 统计棋盘上棋子总数（用于提子判定） */
function countStones(board: number[][] | null): number {
  if (!board) return 0;
  let n = 0;
  for (const row of board) for (const c of row) if (c !== 0) n++;
  return n;
}

/** 坐标文本（列 A-T 跳 I，行从上到下递减） */
function coordLabel(c: Coordinate, size: number): string {
  const letters = 'ABCDEFGHJKLMNOPQRST';
  return `${letters[c.x - 1]}${size - c.y + 1}`;
}

function App() {
  const [view, setView] = useState<View>('lobby');
  const [mode, setMode] = useState<Mode>('create');
  const [boardSize, setBoardSize] = useState<Size | 'auto'>(19);
  const [joinCode, setJoinCode] = useState('');
  const [aiColor, setAiColor] = useState<'black' | 'white'>('black');
  const [aiLevel, setAiLevel] = useState<'auto' | number>('auto');
  const [problems, setProblems] = useState<any[]>([]);
  const [problemLoading, setProblemLoading] = useState(false);
  const [judgeResult, setJudgeResult] = useState<{ achieved: boolean; goal: string; targets: number; deadCount: number } | null>(null);
  const [handicap, setHandicap] = useState(0);
  const [timeControl, setTimeControl] = useState<TimeControl | ''>('');
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [authState, setAuthState] = useState<AuthState>('loading');
  const [me, setMe] = useState<UserInfo | null>(null);

  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [room, setRoom] = useState<RoomState | null>(null);
  const [clock, setClock] = useState<ClockState | null>(null);
  const [lastMove, setLastMove] = useState<Coordinate | null>(null);
  const [myRole, setMyRole] = useState<'host' | 'player' | 'spectator' | null>(null);
  const [cursor, setCursor] = useState<Coordinate | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth <= 900);
  const [soundOn, setSoundOn] = useState(true);

  const socketRef = useRef<Socket | null>(null);
  const tokenRef = useRef<string | null>(localStorage.getItem(TOKEN_KEY));
  const nicknameRef = useRef('');
  const roomCodeRef = useRef<string | null>(null);
  const prevBoardRef = useRef<number[][] | null>(null);
  const soundRef = useRef<SoundManager | null>(null);
  const soundOnRef = useRef(true);
  const prevMoveCountRef = useRef(0);
  const prevStoneCountRef = useRef(0);
  const prevClockMainRef = useRef<{ black: number | null; white: number | null }>({ black: null, white: null });

  const nickname = me?.nickname || '';

  useEffect(() => { nicknameRef.current = nickname; }, [nickname]);

  useEffect(() => { roomCodeRef.current = roomCode; }, [roomCode]);

  // 恢复会话：凭本地 token 拉取本人信息
  useEffect(() => {
    const token = tokenRef.current;
    if (!token) { setAuthState('unauthenticated'); return; }
    fetch(SERVER_URL + '/api/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        if (d.ok) {
          setMe(d.user);
          setAuthState('authenticated');
        } else {
          localStorage.removeItem(TOKEN_KEY);
          tokenRef.current = null;
          setAuthState('unauthenticated');
        }
      })
      .catch(() => setAuthState('unauthenticated'));
  }, []);

  // 建立 socket 连接
  useEffect(() => {
    const socket = io(SERVER_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setError('');
      const token = tokenRef.current;
      if (token) {
        socket.emit('auth', { token }, (res: any) => {
          if (!res?.ok) {
            localStorage.removeItem(TOKEN_KEY);
            tokenRef.current = null;
            setMe(null);
            setAuthState('unauthenticated');
            return;
          }
          // 若之前在房间内，凭账号重连
          if (roomCodeRef.current) {
            socket.emit('reconnect', { roomCode: roomCodeRef.current }, (r: any) => {
              if (!r?.ok) {
                alert('重连失败（房间不存在或已结束），已返回大厅');
                resetToLobby();
              }
            });
          }
        });
      }
    });

    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));

    socket.on('roomState', (s: RoomState) => {
      setRoom(s);
      setClock(s.clock);
      setLastMove(diffBoard(prevBoardRef.current, s.board));
      prevBoardRef.current = s.board;
      // 落子/提子音效（手数增加时判定）
      const mc = s.moveCount ?? 0;
      const sc = countStones(s.board);
      if (mc > prevMoveCountRef.current && soundOnRef.current) {
        const captured = prevStoneCountRef.current + 1 - sc > 0;
        const lastColor = s.moveLog && s.moveLog.length > 0 ? s.moveLog[s.moveLog.length - 1].color : 'black';
        if (captured) soundRef.current?.playStoneCapture();
        else soundRef.current?.playStonePlace(lastColor === 'white' ? 'white' : 'black');
      }
      prevMoveCountRef.current = mc;
      prevStoneCountRef.current = sc;
      // 对局结束：刷新本人战绩（战绩在 finishGame 结算完才广播，此处拉到即最新）
      if (s.status === 'finished') {
        const token = tokenRef.current;
        if (token) {
          fetch(SERVER_URL + '/api/me', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => { if (d.ok) setMe(d.user); })
            .catch(() => {});
        }
        // 等级变化提示（仅本人）
        if (s.rankChanges && nicknameRef.current) {
          const mySide = s.black === nicknameRef.current ? 'black' : s.white === nicknameRef.current ? 'white' : null;
          const mine = mySide ? s.rankChanges.find((rc) => rc.side === mySide) : null;
          if (mine) {
            const dir = mine.delta > 0 ? '升' : '降';
            alert(`等级变化：${mine.beforeLabel} → ${mine.afterLabel}（${dir} ${Math.abs(mine.delta)} 级，${mine.reason}）`);
          }
        }
      }
    });

    socket.on('moveRejected', (e: { error: string }) => {
      alert(`落子被拒: ${e.error}`);
    });

    socket.on('undoRequested', () => {
      if (window.confirm('对方请求悔棋，是否同意？')) {
        socket.emit('respondUndo', { accept: true });
      } else {
        socket.emit('respondUndo', { accept: false });
      }
    });

    socket.on('undoResponded', (d: { accepted: boolean }) => {
      if (!d.accepted) alert('对方拒绝了悔棋请求');
    });

    socket.on('drawRequested', () => {
      if (window.confirm('对方请求和棋，是否同意？')) {
        socket.emit('respondDraw', { accept: true });
      } else {
        socket.emit('respondDraw', { accept: false });
      }
    });

    socket.on('drawResponded', (d: { accepted: boolean }) => {
      if (!d.accepted) alert('对方拒绝了和棋请求');
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 本地平滑倒计时：收到服务端 clock 快照后，每 250ms 递减当前方时间（服务端为权威，落子/结束时校准）
  useEffect(() => {
    if (!clock || room?.status !== 'playing') return;
    const timer = setInterval(() => {
      setClock((prev) => {
        if (!prev) return prev;
        const side = prev.current;
        const c = prev[side];
        if (c.main > 0) {
          return { ...prev, [side]: { ...c, main: Math.max(0, c.main - 0.25) } };
        }
        if (c.byoyomi > 0) {
          return { ...prev, [side]: { ...c, byoyomi: Math.max(0, c.byoyomi - 0.25) } };
        }
        return prev;
      });
    }, 250);
    return () => clearInterval(timer);
  }, [room?.status, !!clock]);

  // 读秒开始提示音：某方基本时间用尽（main 归零）时播放一次
  useEffect(() => {
    if (!clock || room?.status !== 'playing') return;
    (['black', 'white'] as const).forEach((side) => {
      const m = clock[side].main;
      const prev = prevClockMainRef.current[side];
      if (prev != null && prev > 0 && m <= 0 && soundOnRef.current) {
        soundRef.current?.playByoyomi();
      }
      prevClockMainRef.current[side] = m;
    });
  }, [clock, room?.status]);
  // 音效：首次用户手势时初始化（浏览器要求手势解锁 AudioContext）
  useEffect(() => {
    const init = () => {
      if (!soundRef.current) {
        soundRef.current = new SoundManager({ enabled: true, volume: 0.7 });
        soundRef.current.initialize();
      }
    };
    window.addEventListener('pointerdown', init, { once: true });
    return () => window.removeEventListener('pointerdown', init);
  }, []);

  // 移动端判定（宽度 ≤ 900）
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 手机端：进入对局后给光标一个默认位置（天元）
  useEffect(() => {
    if (view === 'game' && isMobile && room?.status === 'playing' && myRole !== 'spectator' && !cursor) {
      const c = Math.floor(((room.boardSize as number) || 19) / 2) + 1;
      setCursor({ x: c, y: c });
    }
  }, [view, isMobile, room?.status, room?.boardSize, myRole, cursor]);

  // 返回大厅：清空身份，并断开重连获得全新 socket（清除旧房间成员身份）
  const resetToLobby = useCallback(() => {
    roomCodeRef.current = null;
    setRoomCode(null);
    setView('lobby');
    setRoom(null);
    setClock(null);
    setLastMove(null);
    setMyRole(null);
    setCursor(null);
    prevClockMainRef.current = { black: null, white: null };
    prevBoardRef.current = null;
    setBusy(false);
    setError('');
    setJoinCode('');
    const s = socketRef.current;
    if (s) {
      s.disconnect();
      s.connect();
    }
  }, []);

  // 登录/注册成功
  const handleAuthed = useCallback((token: string, user: UserInfo) => {
    tokenRef.current = token;
    localStorage.setItem(TOKEN_KEY, token);
    setMe(user);
    setAuthState('authenticated');
    setError('');
    const s = socketRef.current;
    if (s && s.connected) s.emit('auth', { token });
  }, []);

  // 退出登录
  const handleLogout = useCallback(async () => {
    const token = tokenRef.current;
    if (token) {
      try {
        await fetch(SERVER_URL + '/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignore */ }
    }
    localStorage.removeItem(TOKEN_KEY);
    tokenRef.current = null;
    setMe(null);
    setAuthState('unauthenticated');
    resetToLobby();
  }, [resetToLobby]);

  // 进入房间（建房 / 加入 / 观战）
  const enterRoom = () => {
    if (!me) { setError('尚未登录'); return; }
    if ((mode === 'create' || mode === 'ai') && !timeControl) {
      setError('请先选择计时方式');
      return;
    }
    setError('');

    const socket = socketRef.current;
    if (!socket) { setError('尚未连接服务器'); return; }

    if (mode === 'ai') {
      setBusy(true);
      socket.emit('startAI', { boardSize: 'auto', color: aiColor, level: aiLevel, handicap, komi: handicap > 0 ? 0.5 : 7.5, timeControl }, (res: any) => {
        setBusy(false);
        if (!res?.ok) { setError(res?.error || '发起 AI 对弈失败'); return; }
        roomCodeRef.current = res.roomCode;
        setRoomCode(res.roomCode);
        setMyRole('player');
        setView('game');
      });
      return;
    }

    if (mode === 'create') {
      setBusy(true);
      socket.emit('createRoom', { boardSize: boardSize === 'auto' ? 19 : boardSize, handicap, komi: handicap > 0 ? 0.5 : 7.5, timeControl }, (res: any) => {
        setBusy(false);
        if (!res?.ok) { setError(res?.error || '创建房间失败'); return; }
        roomCodeRef.current = res.roomCode;
        setRoomCode(res.roomCode);
        setMyRole('host');
        setView('game');
      });
    } else {
      const code = joinCode.trim();
      if (!code) { setError('请输入房间号'); return; }
      setBusy(true);
      const event = mode === 'join' ? 'joinRoom' : 'spectate';
      socket.emit(event, { roomCode: code }, (res: any) => {
        setBusy(false);
        if (!res?.ok) { setError(res?.error || '进入房间失败'); return; }
        roomCodeRef.current = code;
        setRoomCode(code);
        setMyRole(mode === 'join' ? 'player' : 'spectator');
        setView('game');
      });
    }
  };

  // 我的颜色 / 是否观战
  const myColor = room && nickname
    ? (room.black === nickname ? 'black' : room.white === nickname ? 'white' : null)
    : null;
  const isSpectator = myRole === 'spectator';
  const isPlaying = room?.status === 'playing';
  const isMyTurn = isPlaying && !isSpectator && room?.currentPlayer === myColor;
  const identityText = identityOf(myRole, myColor);

  // 联网动作
  const handleMove = (coord: Coordinate) => {
    const socket = socketRef.current;
    if (!socket || !isMyTurn) return;
    socket.emit('move', { x: coord.x, y: coord.y });
  };

  // 手机端光标：方向键移动 + 确认落子（防误触）
  const bsNum = (room?.boardSize as number) || 19;
  const cursorEmpty = (c: Coordinate | null): boolean => {
    if (!c || !room?.board) return false;
    const row = room.board[c.y - 1];
    return !!row && row[c.x - 1] === StoneColor.EMPTY;
  };
  const moveCursor = (dx: number, dy: number) => {
    setCursor((prev) => {
      const base = prev || { x: Math.floor(bsNum / 2) + 1, y: Math.floor(bsNum / 2) + 1 };
      return {
        x: Math.min(bsNum, Math.max(1, base.x + dx)),
        y: Math.min(bsNum, Math.max(1, base.y + dy)),
      };
    });
  };
  const confirmMove = () => {
    const socket = socketRef.current;
    if (!socket || !isMyTurn || !cursor || !cursorEmpty(cursor)) return;
    socket.emit('move', { x: cursor.x, y: cursor.y });
  };
  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      soundOnRef.current = next;
      if (next) {
        if (!soundRef.current) {
          soundRef.current = new SoundManager({ enabled: true, volume: 0.7 });
          soundRef.current.initialize();
        } else {
          soundRef.current.setEnabled(true);
        }
      } else {
        soundRef.current?.setEnabled(false);
      }
      return next;
    });
  };

  const handlePass = () => {
    const socket = socketRef.current;
    if (!socket || !isMyTurn) return;
    socket.emit('move', { x: null, y: null });
  };

  const handleResign = () => {
    if (!window.confirm('确定要认输吗？')) return;
    socketRef.current?.emit('resign');
  };

  const handleUndo = () => {
    socketRef.current?.emit('requestUndo');
  };

  const handleDraw = () => {
    socketRef.current?.emit('requestDraw');
  };

  const handleCount = () => {
    socketRef.current?.emit('requestCount');
  };

  const respondCount = (accept: boolean) => {
    socketRef.current?.emit('respondCount', { accept });
  };

  // 死活题：加载列表 / 开始做题 / 判定
  const loadProblems = async () => {
    setProblemLoading(true);
    try {
      const res = await fetch(SERVER_URL + '/api/problems');
      const data = await res.json();
      setProblems(Array.isArray(data) ? data : []);
    } catch {
      setProblems([]);
    }
    setProblemLoading(false);
  };

  const startProblem = (problemId: string) => {
    const socket = socketRef.current;
    if (!socket) { setError('尚未连接服务器'); return; }
    socket.emit('startProblem', { problemId }, (res: any) => {
      if (!res?.ok) { setError(res?.error || '开始做题失败'); return; }
      roomCodeRef.current = res.roomCode;
      setRoomCode(res.roomCode);
      setMyRole('player');
      setJudgeResult(null);
      setView('game');
    });
  };

  const handleJudge = () => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('judgeProblem', (res: any) => {
      if (res?.ok) setJudgeResult(res);
      else alert(res?.error || '判定失败');
    });
  };

  // 状态文案
  const statusText = room
    ? (room.status === 'waiting'
        ? '等待对手加入...'
        : room.status === 'finished'
          ? `对局结束 - ${resultText(room.result)}`
          : '对局中')
    : '连接中...';

  const stones: StoneColor[][] = (room?.board as StoneColor[][]) || [];

  if (authState === 'loading') {
    return <div className="loading">加载中...</div>;
  }

  if (authState === 'unauthenticated') {
    return (
      <div className="app">
        <header className="app-header">
          <h1>围棋对战平台</h1>
          <p className="subtitle">房间制联网对战 · 服务端权威引擎</p>
        </header>
        <main className="app-main app-main-lobby">
          <Auth onAuthed={handleAuthed} />
        </main>
        <footer className="app-footer">
          <p>围棋对战平台 © 2026 - 房间制联网对战</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        {view === 'game' ? (
          <div className="game-topbar">
            <div className="gt-players">
              <span className="gt-player"><i className="gt-dot black" />{room?.black || '黑方'}</span>
              <span className="gt-status">{statusText} · {room?.moveCount ?? 0}手</span>
              <span className="gt-player"><i className="gt-dot white" />{room?.white || '白方'}</span>
            </div>
            <div className="gt-right">
              <span className="gt-room">房间 {roomCode ?? '-'}</span>
              <button className="control-btn ghost" onClick={resetToLobby}>离开</button>
            </div>
          </div>
        ) : view === 'review' ? (
          <div className="game-topbar">
            <div className="gt-players">
              <span className="gt-player">棋谱复盘</span>
            </div>
            <div className="gt-right">
              <span className="gt-room">你好，{nickname}</span>
              <button className="control-btn ghost" onClick={handleLogout}>退出登录</button>
            </div>
          </div>
        ) : (
          <>
            <h1>围棋对战平台</h1>
            <div className={`conn-badge ${connected ? 'online' : 'offline'}`}>
              <span className="conn-dot"></span>
              {connected ? '已连接服务器' : '连接断开 / 连接中...'}
            </div>
            <div className="user-bar">
              <span className="user-name">你好，{nickname}</span>
              {me && (
                <span className="user-stats">
                  {me.rankLabel} · {me.stats.wins}胜 {me.stats.losses}负 {me.stats.draws}和 · 胜率 {winRate(me.stats)}
                </span>
              )}
              <button className="logout-btn" onClick={handleLogout}>退出登录</button>
            </div>
            <div className="header-nav">
              <button className="nav-link active">对战</button>
              <button className="nav-link" onClick={() => setView('review')}>棋谱复盘</button>
              <button className="nav-link" onClick={() => { setView('problems'); loadProblems(); }}>死活题</button>
            </div>
          </>
        )}
      </header>

      <main className={view === 'game' ? 'app-main app-main-game' : view === 'review' ? 'app-main app-main-review' : 'app-main app-main-lobby'}>
        {view === 'review' ? (
          <Review onBack={() => setView('lobby')} />
        ) : view === 'problems' ? (
          <div className="lobby">
            <div className="lobby-card problems-card">
              <h2>死活题</h2>
              <p className="problems-sub">共 {problems.length} 题 · 吃子 {problems.filter((p) => p.type === '吃子').length} · 死活 {problems.filter((p) => p.type === '死活').length}</p>
              {problemLoading ? (
                <div className="problems-loading">加载中...</div>
              ) : (
                <div className="problems-list">
                  <h3 className="problems-group">吃子训练</h3>
                  {problems.filter((p) => p.type === '吃子').map((p) => (
                    <button key={p.id} className="problem-item" onClick={() => startProblem(p.id)}>
                      <span className="problem-title">{p.title}</span>
                      <span className="problem-meta">{p.boardSize}路</span>
                    </button>
                  ))}
                  <h3 className="problems-group">死活训练</h3>
                  {problems.filter((p) => p.type === '死活').map((p) => (
                    <button key={p.id} className="problem-item" onClick={() => startProblem(p.id)}>
                      <span className="problem-title">{p.title}</span>
                      <span className="problem-meta">{p.boardSize}路</span>
                    </button>
                  ))}
                </div>
              )}
              <button className="control-btn ghost" onClick={() => setView('lobby')}>返回大厅</button>
            </div>
          </div>
        ) : view === 'lobby' ? (
          <div className="lobby">
            <div className="lobby-card">
              <h2>进入房间</h2>

              <div className="lobby-field">
                <label>方式</label>
                <div className="mode-tabs">
                  <button
                    className={mode === 'create' ? 'active' : ''}
                    onClick={() => setMode('create')}
                  >
                    创建房间
                  </button>
                  <button
                    className={mode === 'join' ? 'active' : ''}
                    onClick={() => setMode('join')}
                  >
                    加入房间
                  </button>
                  <button
                    className={mode === 'spectate' ? 'active' : ''}
                    onClick={() => setMode('spectate')}
                  >
                    观战
                  </button>
                  <button
                    className={mode === 'ai' ? 'active' : ''}
                    onClick={() => setMode('ai')}
                  >
                    AI 对弈
                  </button>
                </div>
              </div>

              {mode === 'create' || mode === 'ai' ? (
                <>
                  {mode === 'create' ? (
                    <div className="lobby-field">
                      <label>棋盘大小</label>
                      <div className="size-tabs">
                        {([13, 19] as Size[]).map((s) => (
                          <button
                            key={s}
                            className={boardSize === s ? 'active' : ''}
                            onClick={() => setBoardSize(s)}
                          >
                            {s} 路
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="lobby-field">
                      <label>棋盘</label>
                      <span className="lobby-hint">跟随难度：25~20级 = 13 路，19 级起 = 19 路</span>
                    </div>
                  )}
                  <div className="lobby-field">
                    <label>让子</label>
                    <select value={handicap} onChange={(e) => setHandicap(Number(e.target.value))}>
                      <option value={0}>不让</option>
                      <option value={2}>让2子</option>
                      <option value={3}>让3子</option>
                      <option value={4}>让4子</option>
                      <option value={5}>让5子</option>
                      <option value={6}>让6子</option>
                      <option value={7}>让7子</option>
                      <option value={9}>让9子</option>
                    </select>
                  </div>
                  <div className="lobby-field">
                    <label>计时</label>
                    <select value={timeControl} onChange={(e) => setTimeControl(e.target.value as TimeControl | '')}>
                      <option value="" disabled>请选择计时</option>
                      {(Object.keys(TIME_CONTROL_LABEL) as TimeControl[]).map((tc) => (
                        <option key={tc} value={tc}>{TIME_CONTROL_LABEL[tc]}</option>
                      ))}
                    </select>
                  </div>
                  {mode === 'ai' && (
                    <>
                      <div className="lobby-field">
                        <label>执子</label>
                        <div className="mode-tabs">
                          <button
                            className={aiColor === 'black' ? 'active' : ''}
                            onClick={() => setAiColor('black')}
                          >
                            执黑（先手）
                          </button>
                          <button
                            className={aiColor === 'white' ? 'active' : ''}
                            onClick={() => setAiColor('white')}
                          >
                            执白（后手）
                          </button>
                        </div>
                      </div>
                      <div className="lobby-field">
                        <label>难度</label>
                        <select value={String(aiLevel)} onChange={(e) => setAiLevel(e.target.value === 'auto' ? 'auto' : Number(e.target.value))}>
                          {AI_LEVEL_OPTIONS.map((o) => (
                            <option key={String(o.v)} value={String(o.v)}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="lobby-field">
                  <label>房间号</label>
                  <input
                    type="text"
                    placeholder="输入 6 位房间号"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value)}
                    maxLength={6}
                  />
                </div>
              )}

              {error && <div className="lobby-error">{error}</div>}

              <button className="lobby-submit" onClick={enterRoom} disabled={busy || !connected || ((mode === 'create' || mode === 'ai') && !timeControl)}>
                {busy ? '处理中...' : mode === 'create' ? '创建房间' : mode === 'join' ? '加入房间' : mode === 'ai' ? '开始 AI 对弈' : '进入观战'}
              </button>
            </div>
          </div>
        ) : (
          <div className="game-container">
            <div className="board-section">
              {room?.problem && (
                <div className="problem-banner">
                  <span className="problem-banner-title">{room.problem.title}</span>
                  <span className="problem-banner-goal">{room.problem.goal === 'live' ? '目标：做活' : '目标：杀棋'}</span>
                </div>
              )}
              {room && room.captureRule > 0 && room.captures && (
                <div className="capture-banner">
                  <span className="capture-banner-rule">吃子棋：先吃满 {room.captureRule} 子胜</span>
                  <span className="capture-banner-count">黑吃 {room.captures.black} : 白吃 {room.captures.white}</span>
                </div>
              )}
              {room?.pendingCount && (
                <div className="count-panel">
                  <div className="count-title">数子确认</div>
                  <div className="count-result">
                    黑 <b>{room.pendingCount.result.black}</b> 子 vs 白 <b>{room.pendingCount.result.white}</b> 子
                    {room.pendingCount.result.dead && room.pendingCount.result.dead.length > 0 && (
                      <span className="count-dead">（死子 {room.pendingCount.result.dead.length} 个）</span>
                    )}
                  </div>
                  <div className="count-legend">□ 方块 = 领地 ｜ ○ 红圈 = 死子</div>
                  <div className="count-actions">
                    <button className="control-btn primary" onClick={() => respondCount(true)}>确认认可</button>
                    <button className="control-btn secondary" onClick={() => respondCount(false)}>拒绝</button>
                  </div>
                </div>
              )}

              {clock && room?.status === 'playing' && (
                <div className="clock-bar">
                  <div className={`clock-side black ${clock.current === 'black' ? 'active' : ''}`}>
                    <span className="clock-label"><i className="gt-dot black" />黑方</span>
                    <span className="clock-time">{sideClockText(clock.black)}</span>
                  </div>
                  <div className={`clock-side white ${clock.current === 'white' ? 'active' : ''}`}>
                    <span className="clock-label"><i className="gt-dot white" />白方</span>
                    <span className="clock-time">{sideClockText(clock.white)}</span>
                  </div>
                </div>
              )}

              <GoBoard
                size={(room?.boardSize as Size) || 19}
                stones={stones}
                onMove={handleMove}
                disabled={!isMyTurn}
                showCoordinates={true}
                highlightLastMove={true}
                lastMove={lastMove}
                showHover={isMyTurn && !isMobile}
                blockOccupied={true}
                cursor={isMobile ? cursor : null}
                cursorMode={isMobile}
                onCursor={setCursor}
                fill={true}
                territory={room?.pendingCount?.result?.territory || null}
              />

              {isMobile && myColor && (
                <div className="move-pad">
                  <div className="pad-row">
                    <div className="pad-cross">
                      <button className="pad-btn up" onClick={() => moveCursor(0, -1)} aria-label="上">▲</button>
                      <button className="pad-btn left" onClick={() => moveCursor(-1, 0)} aria-label="左">◀</button>
                      <button className="pad-btn right" onClick={() => moveCursor(1, 0)} aria-label="右">▶</button>
                      <button className="pad-btn down" onClick={() => moveCursor(0, 1)} aria-label="下">▼</button>
                    </div>
                    <button className="pad-confirm" onClick={confirmMove} disabled={!isMyTurn || !cursorEmpty(cursor)}>
                      <span className="pad-coord">{cursor ? coordLabel(cursor, bsNum) : '—'}</span>
                      <span className="pad-confirm-label">{isMyTurn ? '落子' : '等待'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="info-section">
              <div className="info-panel hide-mobile">
                <h3>对局信息</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">房间号:</span>
                    <span className="info-value">{roomCode ?? '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">棋盘:</span>
                    <span className="info-value">{room?.boardSize ?? 19}路</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">贴目:</span>
                    <span className="info-value">7.5目</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">手数:</span>
                    <span className="info-value">{room?.moveCount ?? 0}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">身份:</span>
                    <span className="info-value">{identityText}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">状态:</span>
                    <span className="info-value">
                      {room?.status === 'waiting' ? '等待' : room?.status === 'playing' ? '对局中' : '已结束'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="info-panel hide-mobile">
                <h3>观战者</h3>
                {room && room.spectators.length > 0 ? (
                  <div className="spectator-list">
                    {room.spectators.map((s, i) => (
                      <span key={i} className="spectator-tag">{s}</span>
                    ))}
                  </div>
                ) : (
                  <div className="empty-spectator">暂无观战者</div>
                )}
              </div>

              {room?.result && (
                <div className="info-panel result-panel">
                  <h3>对局结果</h3>
                  <div className="result-text">{resultText(room.result)}</div>
                  {room.result.blackScore != null && room.result.whiteScore != null && (
                    <div className="result-score">
                      黑 {room.result.blackScore} 子 : 白 {room.result.whiteScore} 子
                    </div>
                  )}
                  <div className="result-reason">（{REASON_TEXT[room.result.reason] || room.result.reason}）</div>
                </div>
              )}

              {judgeResult && (
                <div className="info-panel result-panel">
                  <h3>判定结果</h3>
                  <div className="result-text">{judgeResult.achieved ? '✅ 达成目标' : '❌ 未达成'}</div>
                  <div className="result-reason">（目标{judgeResult.goal === 'live' ? '做活' : '杀棋'} · 目标{judgeResult.targets}子 · 死{judgeResult.deadCount}子）</div>
                </div>
              )}

              <div className="board-controls">
                <button className="control-btn secondary" onClick={handleUndo} disabled={!isMyTurn}>悔棋</button>
                <button className="control-btn secondary" onClick={handlePass} disabled={!isMyTurn}>停一手</button>
                <button className="control-btn secondary" onClick={handleDraw} disabled={!isMyTurn}>和棋</button>
                <button className="control-btn secondary" onClick={handleCount} disabled={!isPlaying || isSpectator || !!room?.pendingCount}>数子</button>
                <button className="control-btn danger" onClick={handleResign} disabled={!isPlaying || isSpectator}>认输</button>
                {room?.problem && (
                  <button className="control-btn primary" onClick={handleJudge} disabled={!isPlaying}>判定</button>
                )}
                <button className="control-btn ghost" onClick={toggleSound}>{soundOn ? '🔊 音效开' : '🔇 音效关'}</button>
                <button className="control-btn ghost" onClick={resetToLobby}>离开房间</button>
              </div>
            </div>
          </div>
        )}
      </main>

      {view !== 'game' && (
        <footer className="app-footer">
          <p>围棋对战平台 © 2026 - 房间制联网对战</p>
          <p className="footer-links">
            <a href="#" onClick={(e) => { e.preventDefault(); alert('功能开发中'); }}>使用说明</a>
            <span> | </span>
            <a href="#" onClick={(e) => { e.preventDefault(); alert('功能开发中'); }}>关于我们</a>
            <span> | </span>
            <a href="#" onClick={(e) => { e.preventDefault(); alert('功能开发中'); }}>问题反馈</a>
          </p>
        </footer>
      )}
    </div>
  );
}

/** 身份文本 */
function identityOf(myRole: 'host' | 'player' | 'spectator' | null, myColor: 'black' | 'white' | null): string {
  if (myRole === 'spectator') return '观战';
  if (myColor === 'black') return '黑方';
  if (myColor === 'white') return '白方';
  if (myRole === 'host') return '房主';
  return '对局者';
}

/** 结果文本：黑胜 / 白胜 / 和棋，带比分差则附数字 */
function resultText(result: { winner: string; reason: string; margin?: number } | null): string {
  if (!result) return '';
  const name = result.winner === 'black' ? '黑胜' : result.winner === 'white' ? '白胜' : '和棋';
  if (result.margin != null && result.winner !== 'draw') {
    const m = Math.round(result.margin * 100) / 100;
    return `${name} ${m} 子`;
  }
  return name;
}

/** 秒数格式化：>=60s 显示 m:ss，否则读秒显示 N秒 */
function formatClockSeconds(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  }
  return `${s}秒`;
}

/** 单方计时文本：基本时间用尽则显示读秒 + 剩余容错次数 */
function sideClockText(side: ClockSide): string {
  if (side.main > 0) return formatClockSeconds(side.main);
  return `读秒 ${Math.ceil(Math.max(0, side.byoyomi))}秒${side.periods >= 0 ? ` (剩${side.periods})` : ''}`;
}

/** 胜率：无对局返回 '—' */
function winRate(stats: { games: number; wins: number }): string {
  if (!stats.games) return '—';
  return `${Math.round((stats.wins / stats.games) * 100)}%`;
}

/** AI 难度档（与后端 aiEngineFor 对齐：自动匹配 + 32档 strength 0~31） */
const AI_LEVEL_OPTIONS: { v: 'auto' | number; label: string }[] = [
  { v: 'auto', label: '自动匹配（按我的等级）' },
  ...Array.from({ length: 32 }, (_, s) => ({ v: s, label: rankLabel(s) })),
];

export default App;
